import React, { useEffect, useState } from 'react';
import { Link, useNavigate, Routes, Route } from 'react-router-dom';
import { User, Package, Settings, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

function ProfileTab() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', address: user?.address || '', city: user?.city || '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put('/auth/profile', form);
      updateUser(res.data);
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update profile'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h2 className="text-xl font-serif font-semibold mb-6">My Profile</h2>
      <form onSubmit={handleSubmit} className="max-w-md space-y-4">
        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Full Name</label>
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900" />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Email</label>
          <input value={user?.email || ''} disabled className="w-full border border-gray-200 px-4 py-3 text-sm bg-gray-50 text-gray-500" />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Phone</label>
          <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900" />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">Address</label>
          <input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900" />
        </div>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wider text-gray-600 mb-1">City</label>
          <input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-gray-900" />
        </div>
        <button type="submit" disabled={loading} className="bg-gray-900 text-white px-8 py-3 text-sm font-medium uppercase tracking-wide hover:bg-gray-700 transition-colors disabled:opacity-60">
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/orders').then(r => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    processing: 'bg-purple-100 text-purple-800',
    shipped: 'bg-indigo-100 text-indigo-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  if (loading) return <div className="animate-pulse space-y-4">{Array(3).fill(null).map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded" />)}</div>;

  return (
    <div>
      <h2 className="text-xl font-serif font-semibold mb-6">My Orders</h2>
      {orders.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Package size={48} className="mx-auto text-gray-300 mb-4" />
          <p>No orders yet</p>
          <Link to="/" className="mt-4 inline-block text-gray-900 underline text-sm">Start Shopping</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.id} className="border border-gray-100 p-5 hover:border-gray-300 transition-colors">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{order.order_number}</p>
                  <p className="text-xs text-gray-500 mt-1">{new Date(order.created_at).toLocaleDateString('en-PK', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1.5 rounded-full capitalize ${statusColors[order.status] || 'bg-gray-100 text-gray-700'}`}>
                  {order.status}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap justify-between items-end">
                <div>
                  <p className="text-xs text-gray-500">{order.items?.length || 0} item(s)</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">Rs.{Number(order.total).toLocaleString()}</p>
                </div>
                <div className="text-xs text-gray-500">
                  <p>Payment: <span className="capitalize font-medium">{order.payment_method} — {order.payment_status}</span></p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AccountPage() {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');

  useEffect(() => {
    if (!isAuthenticated) navigate('/login');
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const tabs = [
    { key: 'orders', label: 'My Orders', icon: Package },
    { key: 'profile', label: 'Profile', icon: User },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 md:py-12">
      <h1 className="text-3xl font-serif font-semibold text-gray-900 mb-8">My Account</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        {/* Sidebar */}
        <aside className="md:col-span-1">
          <div className="bg-gray-50 p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center text-white font-semibold text-lg">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-gray-900 text-sm">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
          </div>
          <nav className="space-y-1">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors text-left ${activeTab === tab.key ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors text-left"
            >
              <LogOut size={16} />
              Logout
            </button>
          </nav>
        </aside>

        {/* Content */}
        <main className="md:col-span-3">
          {activeTab === 'profile' ? <ProfileTab /> : <OrdersTab />}
        </main>
      </div>
    </div>
  );
}
